// Column picker — reusable dropdown that toggles report-column visibility.
// Pinned columns are always shown and not toggleable (no checkbox).
//
// Props:
//   columns     — [{ id, label, pinned?: bool, hint?: string }]
//   visibleIds  — string[] of currently-visible non-pinned ids
//   onChange    — (nextVisibleIds: string[]) => void

import React from 'react';
import { ActionIcon, Checkbox, Divider, Group, Popover, Stack, Text, Tooltip } from '@mantine/core';
import { IconColumns3 } from '@tabler/icons-react';

export function ColumnPicker({ columns, visibleIds, onChange }) {
  const toggleable = columns.filter((c) => !c.pinned);
  const pinned = columns.filter((c) => c.pinned);
  const visibleSet = new Set(visibleIds);

  function toggle(id) {
    if (visibleSet.has(id)) {
      onChange(visibleIds.filter((x) => x !== id));
    } else {
      // Preserve original column order so the table re-shows the column in-place.
      const nextVisible = [...visibleIds, id];
      const originalOrder = columns.map((c) => c.id);
      nextVisible.sort((a, b) => originalOrder.indexOf(a) - originalOrder.indexOf(b));
      onChange(nextVisible);
    }
  }

  function selectAll() {
    onChange(toggleable.map((c) => c.id));
  }

  function selectNone() {
    onChange([]);
  }

  return (
    <Popover width={280} position="bottom-end" withinPortal shadow="md">
      <Popover.Target>
        <Tooltip label="Choose columns">
          <ActionIcon variant="light" size="md">
            <IconColumns3 size={16} />
          </ActionIcon>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap={6}>
          <Group justify="space-between">
            <Text size="xs" fw={600}>Columns</Text>
            <Group gap={6}>
              <Text size="xs" c="dimmed" style={{ cursor: 'pointer' }} onClick={selectAll}>all</Text>
              <Text size="xs" c="dimmed">·</Text>
              <Text size="xs" c="dimmed" style={{ cursor: 'pointer' }} onClick={selectNone}>none</Text>
            </Group>
          </Group>
          <Divider />
          {pinned.length > 0 && (
            <>
              {pinned.map((c) => (
                <Group key={c.id} gap="xs">
                  <Checkbox checked disabled size="xs" />
                  <Text size="xs" c="dimmed">{c.label} (pinned)</Text>
                </Group>
              ))}
              <Divider />
            </>
          )}
          {toggleable.map((c) => (
            <Group key={c.id} gap="xs" wrap="nowrap">
              <Checkbox
                checked={visibleSet.has(c.id)}
                onChange={() => toggle(c.id)}
                size="xs"
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text size="xs">{c.label}</Text>
                {c.hint && <Text size="xs" c="dimmed">{c.hint}</Text>}
              </div>
            </Group>
          ))}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
